import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/lib/siteI18n";
import { LangSwitcher } from "@/components/LangSwitcher";

const NAV_KEYS = [
  ["home", "/"], ["services", "/services"], ["elite", "/coaching-elite"], ["souverain", "/coaching-souverain"],
  ["kit", "/kit-digital-premium"], ["analyse", "/analyse-de-precision"], ["journal", "/journal"], ["about", "/a-propos"], ["contact", "/contact"],
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();
  const NAV = NAV_KEYS.map(([k, to]) => ({ label: t.nav[k], to }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-brand-ink/80 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="font-serif text-2xl md:text-3xl tracking-tight text-white">
            AETHERIS
          </span>
          <span className="hidden md:inline text-[10px] uppercase tracking-luxe text-gold pt-1">
            Académie
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace(/\//g, "") || "home"}`}
              className={`text-sm uppercase tracking-[0.15em] transition-colors ${
                location.pathname === item.to ? "text-gold" : "text-white/60 hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher compact id="header-lang" />
          <Link
            to="/portal-access"
            data-testid="nav-portal"
            className="text-sm uppercase tracking-[0.15em] text-white/60 hover:text-white transition-colors"
          >
            {t.nav.portal}
          </Link>
          <Link
            to="/diagnostic-inaugural"
            data-testid="header-cta-diagnostic"
            className="bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold"
          >
            {t.nav.cta}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-brand-ink border-t border-white/5"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                  className="text-lg font-serif text-white/80"
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/portal-access" className="text-lg font-serif text-white/80">
                {t.nav.portal}
              </Link>
              <LangSwitcher id="mobile-lang" />
              <Link
                to="/diagnostic-inaugural"
                data-testid="mobile-cta-diagnostic"
                onClick={() => setOpen(false)}
                className="bg-gold text-brand-ink px-6 py-3 text-center text-xs uppercase tracking-[0.15em] font-semibold"
              >
                {t.nav.cta}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
